import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    completeMovmoOAuth,
    MissingVerifierError,
    StateMismatchError,
} from "@movmo/connect-button";
import { linkCompleteWithSession } from "../../services/demoAirlineApi";
import { buildRedirectUri } from "../../config/movmoIdentity";
import type { MovmoDeclinedState } from "../../components/MovmoIdentityChip";

type Status = "pending" | "error";

/**
 * OAuth callback for Aero's one-click Movmo identity chip. Registered at the
 * EXACT path aero-demo-airline's OAuth client has provisioned as its
 * redirect_uri — see src/config/movmoIdentity.ts.
 *
 * Flow: completeMovmoOAuth() validates state (CSRF) + retrieves the PKCE
 * verifier, then this stands in for "the airline's own backend" (mock-airline
 * has none) by calling monolith-api's demo-airline /link/complete directly —
 * exactly like a real partner's server would exchange the code for a
 * movmo_guid, except a real partner does this server-side with a persistent
 * customer session instead of the silent demo-session mint used here (see
 * demoAirlineApi.ts's SIMULATION STUB comment).
 *
 * Once the link completes the Aero session carries the traveler's Movmo
 * identity, so the landing page greets them from GET /me (Flow B): this page
 * navigates straight there. Declining on the Movmo
 * consent screen (?error=access_denied) returns to the landing page with a
 * neutral notice.
 */
export default function AeroConnectCallback() {
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>("pending");
    const [message, setMessage] = useState(
        "Connecting your Movmo account…",
    );
    // React 18 StrictMode double-invokes effects in dev; the OAuth code is
    // single-use, so a second pass would fail with "already redeemed".
    // Mirrors ConnectButtonCallback.tsx / AeroCheckout's hold-call guard.
    const didRun = useRef(false);

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const state = params.get("state");
        const errorParam = params.get("error");

        if (errorParam) {
            // access_denied: the traveler declined on the Movmo consent screen.
            // Back to the landing page with a neutral notice (nothing was
            // shared, nothing to retry). Any other auth-server error also
            // returns to the landing page, where the chip is still offered.
            navigate("/aero", {
                replace: true,
                state:
                    errorParam === "access_denied"
                        ? ({ movmoDeclined: true } as MovmoDeclinedState)
                        : undefined,
            });
            return;
        }
        if (!code || !state) {
            setStatus("error");
            setMessage("Missing code or state in the callback URL.");
            return;
        }

        completeMovmoOAuth(code, state)
            .then((completion) =>
                linkCompleteWithSession("aero", {
                    code: completion.code,
                    code_verifier: completion.codeVerifier,
                    redirect_uri: buildRedirectUri("aero"),
                }),
            )
            .then(() => {
                navigate("/aero", { replace: true });
            })
            .catch((err) => {
                if (
                    err instanceof StateMismatchError ||
                    err instanceof MissingVerifierError
                ) {
                    console.error(
                        "[aero] Movmo connect callback rejected:",
                        err,
                    );
                    navigate("/aero", { replace: true });
                    return;
                }
                console.error("[aero] Movmo connect-link failed:", err);
                setStatus("error");
                setMessage(
                    "We couldn't connect your Movmo account. Please try again.",
                );
            });
    }, [navigate]);

    return (
        <div
            className="min-h-screen flex items-center justify-center"
            style={{
                backgroundColor: "#1A1A1A",
                fontFamily:
                    '"Haas Grot Disp Web", "Haas Grot Text Web", "Helvetica Neue", Helvetica, Arial, sans-serif',
            }}
        >
            <div
                style={{
                    maxWidth: 420,
                    padding: 32,
                    textAlign: "center",
                }}
            >
                {status === "pending" && (
                    <div
                        className="mx-auto mb-4 animate-spin rounded-full"
                        style={{
                            width: 32,
                            height: 32,
                            border: "3px solid #282828",
                            borderTopColor: "#fb6900",
                        }}
                    />
                )}
                <p style={{ color: "#fff", fontSize: 16 }}>{message}</p>
                {status === "error" && (
                    <button
                        onClick={() => navigate("/aero", { replace: true })}
                        style={{
                            marginTop: 20,
                            padding: "10px 20px",
                            backgroundColor: "#fb6900",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            cursor: "pointer",
                            fontWeight: 500,
                        }}
                    >
                        Back to Aero
                    </button>
                )}
            </div>
        </div>
    );
}
